const TO_RADIANS = Math.PI / 180

const SAFE_LONG_SIDE = 2048
const MAX_CANVAS_SIDE = 3072

async function makeSafeBitmapFromImage(img) {
    const naturalW = img.naturalWidth || img.width
    const naturalH = img.naturalHeight || img.height

    const longSide = Math.max(naturalW, naturalH)
    const shrink = longSide > SAFE_LONG_SIDE ? SAFE_LONG_SIDE / longSide : 1

    const safeW = Math.max(1, Math.floor(naturalW * shrink))
    const safeH = Math.max(1, Math.floor(naturalH * shrink))

    const src = document.createElement('canvas')
    src.width = safeW
    src.height = safeH

    const sctx = src.getContext('2d', { willReadFrequently: false })
    sctx.imageSmoothingQuality = 'high'
    sctx.drawImage(img, 0, 0, safeW, safeH)

    return {
        sourceCanvas: src,
        scaleFromNatural: shrink,
    }
}

/**
 * Robust iOS-safe preview renderer with rotate & zoom.
 */
export async function canvasPreview(image, canvas, crop, zoom = 1, rotate = 0) {
    if (!image || !canvas || !crop?.width || !crop?.height) return

    const { sourceCanvas, scaleFromNatural } = await makeSafeBitmapFromImage(image)

    const displayedW = image.width
    const displayedH = image.height
    const naturalW = image.naturalWidth || image.width
    const naturalH = image.naturalHeight || image.height

    const displayedToNaturalX = naturalW / displayedW
    const displayedToNaturalY = naturalH / displayedH

    const naturalCropX = crop.x * displayedToNaturalX
    const naturalCropY = crop.y * displayedToNaturalY
    const naturalCropW = crop.width * displayedToNaturalX
    const naturalCropH = crop.height * displayedToNaturalY

    const safeCropX = naturalCropX * scaleFromNatural
    const safeCropY = naturalCropY * scaleFromNatural
    const safeCropW = naturalCropW * scaleFromNatural
    const safeCropH = naturalCropH * scaleFromNatural

    let outW = Math.max(1, Math.floor(safeCropW * zoom))
    let outH = Math.max(1, Math.floor(safeCropH * zoom))
    const cap = Math.max(outW, outH) > MAX_CANVAS_SIDE ? MAX_CANVAS_SIDE / Math.max(outW, outH) : 1
    outW = Math.floor(outW * cap)
    outH = Math.floor(outH * cap)

    const dpr = 1 // iOS-safe

    canvas.width = outW * dpr
    canvas.height = outH * dpr

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.scale(dpr, dpr)

    const cropCx = safeCropX + safeCropW / 2
    const cropCy = safeCropY + safeCropH / 2

    const rad = rotate * TO_RADIANS
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    const rotW = Math.abs(safeCropW * cos) + Math.abs(safeCropH * sin)
    const rotH = Math.abs(safeCropW * sin) + Math.abs(safeCropH * cos)

    ctx.save()
    ctx.translate(outW / 2, outH / 2)
    ctx.rotate(rad)
    ctx.scale(zoom, zoom)
    ctx.translate(-cropCx, -cropCy)
    ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height)
    ctx.restore()
}
